import { enUS, pl } from 'date-fns/locale'
import { Translations } from './messages'

declare global {
	interface Navigator {
		userLanguage?: string
		browserLanguage?: string
	}
}

/**
 * The languages the apps are translated into. Everything that depends on the
 * language - messages, date-fns and Intl formatting, the locale sent to the
 * backend - is derived from one of these, so they can never disagree.
 */
export const SUPPORTED_LOCALES = ['en', 'pl'] as const
export type AppLocale = typeof SUPPORTED_LOCALES[number]

const FALLBACK: AppLocale = 'en'
const STORAGE_KEY = 'locale'

/** date-fns conventions for each app locale. */
const FNS_LOCALES: Record<AppLocale, Locale> = { en: enUS, pl }

const isAppLocale = (tag: string): tag is AppLocale =>
	(SUPPORTED_LOCALES as ReadonlyArray<string>).includes(tag)

/**
 * The first requested language tag the apps support, matched on the whole tag
 * and then on its language alone: "pl-PL" is Polish, "en_GB" is English.
 * Browsers report regional tags, so an exact match alone sends every Polish
 * browser to the English fallback.
 */
export const resolveLocale = (
	requested: ReadonlyArray<string | null | undefined>
): AppLocale => {
	for (const tag of requested) {
		const normalized = tag?.trim().toLowerCase().replace(/_/g, '-')
		if (!normalized) {
			continue
		}
		if (isAppLocale(normalized)) {
			return normalized
		}
		const language = normalized.split('-')[0]
		if (isAppLocale(language)) {
			return language
		}
	}
	return FALLBACK
}

const storedLocale = (): string | null => {
	try {
		return localStorage.getItem(STORAGE_KEY)
	} catch {
		return null
	}
}

/** The user's own choice first, then the browser's languages in order. */
const requestedLocales = (): Array<string | null | undefined> => [
	storedLocale(),
	...(typeof navigator === 'undefined'
		? []
		: [
				...(navigator.languages ?? []),
				navigator.language,
				navigator.userLanguage,
				navigator.browserLanguage,
		  ]),
]

/**
 * The app locale - the language of the UI, and what the backend is told for
 * e-mails and invitations. Not for Intl date and time formatting; see
 * `getFormattingLocale`.
 */
export const getLocale = (): AppLocale => resolveLocale(requestedLocales())

/** Intl throws a RangeError on a tag it cannot parse. */
const isValidTag = (tag: string): boolean => {
	try {
		Intl.DateTimeFormat(tag)
		return true
	} catch {
		return false
	}
}

/** The region subtag of a language tag: "DE" in "de-DE", "419" in "es-419". */
const regionOf = (tag: string): string | undefined => {
	const [, ...subtags] = tag.trim().replace(/_/g, '-').split('-')
	const afterScript = /^[a-z]{4}$/i.test(subtags[0] ?? '')
		? subtags.slice(1)
		: subtags
	const region = afterScript[0]
	return region && /^([a-z]{2}|\d{3})$/i.test(region)
		? region.toUpperCase()
		: undefined
}

/**
 * The locale for Intl date and time formatting: the language of the UI with
 * the browser's region - "en-DE", "pl-GB". The language keeps weekday and
 * month names in the language of the screen around them; the region keeps the
 * clock and the day-month order the user is used to.
 *
 * Deliberately not `getLocale` alone: "en" or "pl" turns a German or British
 * user's 24-hour clock into AM/PM and their dates into US month-first order,
 * although the UI is only translated into two languages.
 */
export const getFormattingLocale = (): string => {
	const language = getLocale()
	const region = requestedLocales()
		.map(tag => (tag ? regionOf(tag) : undefined))
		.find(Boolean)
	const tag = region ? `${language}-${region}` : language
	return isValidTag(tag) ? tag : language
}

/** date-fns conventions for the app locale. */
export const getFnsLocale = (): Locale => FNS_LOCALES[getLocale()]

/** Pins the app locale, or with `null` goes back to the browser's. */
export const setLocale = (locale: AppLocale | null): void => {
	try {
		if (locale) {
			localStorage.setItem(STORAGE_KEY, locale)
		} else {
			localStorage.removeItem(STORAGE_KEY)
		}
	} catch {
		// Storage can be blocked; the browser's language still applies.
	}
}

export const getMessages = (
	translations: Translations,
	locale: string
): Record<string, string> =>
	translations[resolveLocale([locale])] || translations[FALLBACK]
